import client from "./client";

// ────────── Types ──────────

export type IntegrationProvider =
  | "SLACK"
  | "GOOGLE_CALENDAR"
  | "OUTLOOK_CALENDAR"
  | "PAYROLL"
  | "BACKGROUND_CHECK";

export type IntegrationStatus = "DISCONNECTED" | "CONNECTED" | "ERROR";

export interface IntegrationProviderInfo {
  provider: IntegrationProvider;
  label: string;
  category: string;
  supports_oauth: boolean;
  supports_webhook: boolean;
  docs_url: string | null;
}

export interface IntegrationRead {
  id: string;
  provider: IntegrationProvider;
  status: IntegrationStatus;
  is_enabled: boolean;
  account_label: string | null;
  scopes: string | null;
  config: Record<string, unknown>;
  last_error: string | null;
  last_synced_at: string | null;
  webhook_url: string | null;
  has_webhook_secret: boolean;
  created_at: string;
  updated_at: string;
}

export interface ConnectResponse {
  authorize_url: string;
  state: string;
}

export interface IntegrationEvent {
  id: string;
  provider: IntegrationProvider;
  direction: "inbound" | "outbound";
  event_type: string;
  status: string;
  payload: Record<string, unknown>;
  error: string | null;
  created_at: string;
}

// ────────── API calls ──────────

export async function listProviders(): Promise<IntegrationProviderInfo[]> {
  const { data } = await client.get<IntegrationProviderInfo[]>(
    "/integrations/providers",
  );
  return data;
}

export async function listIntegrations(): Promise<IntegrationRead[]> {
  const { data } = await client.get<IntegrationRead[]>("/integrations");
  return data;
}

export async function connectIntegration(
  provider: IntegrationProvider,
): Promise<ConnectResponse> {
  const { data } = await client.post<ConnectResponse>(
    `/integrations/${provider.toLowerCase()}/connect`,
  );
  return data;
}

export async function disconnectIntegration(
  provider: IntegrationProvider,
): Promise<IntegrationRead> {
  const { data } = await client.delete<IntegrationRead>(
    `/integrations/${provider.toLowerCase()}`,
  );
  return data;
}

export async function updateIntegration(
  provider: IntegrationProvider,
  payload: {
    config?: Record<string, unknown>;
    account_label?: string;
    is_enabled?: boolean;
  },
): Promise<IntegrationRead> {
  const { data } = await client.patch<IntegrationRead>(
    `/integrations/${provider.toLowerCase()}`,
    payload,
  );
  return data;
}

export async function rotateWebhookSecret(
  provider: IntegrationProvider,
): Promise<IntegrationRead> {
  const { data } = await client.post<IntegrationRead>(
    `/integrations/${provider.toLowerCase()}/rotate-secret`,
  );
  return data;
}

export async function revealWebhookSecret(
  provider: IntegrationProvider,
): Promise<{ webhook_secret: string }> {
  const { data } = await client.get<{ webhook_secret: string }>(
    `/integrations/${provider.toLowerCase()}/secret`,
  );
  return data;
}

export async function listIntegrationEvents(
  provider: IntegrationProvider,
  limit = 50,
): Promise<IntegrationEvent[]> {
  const { data } = await client.get<IntegrationEvent[]>(
    `/integrations/${provider.toLowerCase()}/events`,
    { params: { limit } },
  );
  return data;
}
